import { useState, useRef, useEffect, useCallback } from 'react';
import { X, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

let faceModel = null;
async function loadFaceModel() {
  if (faceModel) return faceModel;
  const tf = await import('@tensorflow/tfjs');
  try { await tf.setBackend('webgl'); } catch (e) { /* fallback */ }
  await tf.ready();
  const blazeface = await import('@tensorflow-models/blazeface');
  faceModel = await blazeface.load();
  return faceModel;
}

/**
 * Front-camera face capture with a corner-bracket guide frame.
 * Crops to the square guide frame and returns a compressed JPEG File.
 */
export function FaceCamera({ onCapture, onClose }) {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const capturedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const [autoFace, setAutoFace] = useState(true);
  const [faceProgress, setFaceProgress] = useState(0);
  const [faceOk, setFaceOk] = useState(false);
  const [moving, setMoving] = useState(false);
  const faceStableRef = useRef(0);
  const prevFaceRef = useRef(null);

  const startCamera = useCallback(async (facing) => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setReady(true);
      }
    } catch (err) {
      console.error('Camera error:', err);
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    startCamera(facingMode);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, [facingMode, startCamera]);

  // Square guide frame mapped from CSS coords to native video pixels (object-cover).
  const computeCropRect = useCallback((marginCss = 0) => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const vW = video.videoWidth;
    const vH = video.videoHeight;
    const rect = (containerRef.current || video).getBoundingClientRect();
    const cW = rect.width;
    const cH = rect.height;
    if (!cW || !cH) return null;
    // Effective scale includes the manual zoom applied to the video preview.
    const effScale = Math.max(cW / vW, cH / vH) * zoomRef.current;
    const offX = (vW * effScale - cW) / 2;
    const offY = (vH * effScale - cH) / 2;
    // 3:4 portrait guide frame (width : height = 3 : 4).
    let frameW = 0.72 * cW;
    let frameH = frameW * 4 / 3;
    if (frameH > 0.8 * cH) { frameH = 0.8 * cH; frameW = frameH * 3 / 4; }
    const frameLeft = (cW - frameW) / 2;
    const frameTop = (cH - frameH) / 2;
    let sx = (frameLeft - marginCss + offX) / effScale;
    let sy = (frameTop - marginCss + offY) / effScale;
    let sw = (frameW + marginCss * 2) / effScale;
    let sh = (frameH + marginCss * 2) / effScale;
    sx = Math.max(0, Math.min(sx, vW));
    sy = Math.max(0, Math.min(sy, vH));
    sw = Math.max(1, Math.min(sw, vW - sx));
    sh = Math.max(1, Math.min(sh, vH - sy));
    return { sx, sy, sw, sh };
  }, []);

  const handleCapture = useCallback((force = false) => {
    if (capturedRef.current && force !== true) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    // Crop out to the OUTER frame (14px outside the inner guide frame).
    let r = computeCropRect(14);
    if (!r) {
      // Fallback: center 3:4 crop of the full video frame so the button always works.
      const vW = video.videoWidth, vH = video.videoHeight;
      let sw = vW, sh = sw * 4 / 3;
      if (sh > vH) { sh = vH; sw = sh * 3 / 4; }
      r = { sx: (vW - sw) / 2, sy: (vH - sh) / 2, sw, sh };
    }
    capturedRef.current = true;

    const target = Math.min(720, Math.round(r.sw));
    canvas.width = target;
    canvas.height = Math.round(target * (r.sh / r.sw));
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, r.sx, r.sy, r.sw, r.sh, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      if (!blob) { capturedRef.current = false; return; }
      const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
      try {
        const ok = await onCapture(file);
        // On success the parent unmounts this component (stream stops on cleanup).
        // On failure keep the camera open and allow the user to retry.
        if (ok === false) capturedRef.current = false;
      } catch (e) {
        capturedRef.current = false;
      }
    }, 'image/jpeg', 0.9);
  }, [computeCropRect, onCapture]);

  // Auto capture: shoot when a face is centered inside the circle and held steady.
  useEffect(() => {
    if (!ready || !autoFace) { setFaceProgress(0); setFaceOk(false); return; }
    let cancelled = false;
    let timer = null;
    const NEEDED = 3; // ~1s (400ms interval)
    faceStableRef.current = 0;
    capturedRef.current = false;
    (async () => {
      let model;
      try { model = await loadFaceModel(); } catch (e) { return; }
      if (cancelled) return;
      const tick = async () => {
        if (cancelled || capturedRef.current) return;
        const video = videoRef.current;
        const r = computeCropRect();
        let good = false;
        let moved = false;
        if (video && video.videoWidth && r) {
          try {
            const preds = await model.estimateFaces(video, false);
            if (preds && preds.length > 0) {
              const p = preds[0];
              const [x1, y1] = p.topLeft;
              const [x2, y2] = p.bottomRight;
              const fcx = (x1 + x2) / 2, fcy = (y1 + y2) / 2, fw = x2 - x1;
              const circleD = 0.84 * r.sw;
              const ccx = r.sx + 0.08 * r.sw + circleD / 2;
              const ccy = r.sy + 0.17 * r.sh + circleD / 2;
              const rad = circleD / 2;
              const dist = Math.hypot(fcx - ccx, fcy - ccy);
              const centered = dist < rad * 0.5;
              // Auto-zoom so the face fills the circle to a target ratio.
              const TARGET = 0.72;
              const ratio = fw / circleD;
              if (ratio > 0.05 && Math.abs(ratio - TARGET) > 0.08) {
                const idealZoom = zoomRef.current * (TARGET / ratio);
                const nz = Math.max(0.5, Math.min(3, zoomRef.current + (idealZoom - zoomRef.current) * 0.4));
                setZoom(parseFloat(nz.toFixed(3)));
              }
              const sizeOk = Math.abs(ratio - TARGET) < 0.15;
              good = centered && sizeOk;
              // Movement detection (blink red if the user moves).
              const pf = prevFaceRef.current;
              if (pf) {
                const mv = Math.hypot(fcx - pf.x, fcy - pf.y);
                moved = mv > circleD * 0.06;
              }
              prevFaceRef.current = { x: fcx, y: fcy };
            }
          } catch (e) { /* ignore */ }
        }
        if (cancelled) return;
        setMoving(moved);
        faceStableRef.current = good ? faceStableRef.current + 1 : 0;
        setFaceOk(good);
        setFaceProgress(Math.min(1, faceStableRef.current / NEEDED));
        if (faceStableRef.current >= NEEDED) {
          handleCapture();
          return;
        }
        timer = setTimeout(tick, 400);
      };
      tick();
    })();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, autoFace, facingMode, computeCropRect, handleCapture]);

  return (
    <div ref={containerRef} className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="face-camera">
      <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }} />
      <canvas ref={canvasRef} className="hidden" />

      {/* Close */}
      <button onClick={onClose} className="absolute top-4 right-4 z-[3] w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center" data-testid="face-camera-close">
        <X className="h-5 w-5" />
      </button>

      {/* Auto capture toggle */}
      <button
        type="button"
        onClick={() => setAutoFace(v => !v)}
        className={`absolute top-4 left-4 z-[3] px-3 py-1 rounded-full text-xs font-medium border ${autoFace ? 'bg-white text-black border-white' : 'bg-black/40 text-white border-white/40'}`}
        data-testid="face-camera-auto-toggle"
      >
        {t('register.camAuto')}: {autoFace ? 'ON' : 'OFF'}
      </button>

      {/* Corner-bracket guide frame */}
      <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 2 }}>
        <div className="relative" style={{ width: '72vw', aspectRatio: '3 / 4', maxWidth: '360px', maxHeight: '80%' }}>
          {/* outer decorative frame (frame outside the frame) */}
          <div className="absolute rounded-xl border-2 border-[#00A651]/70" style={{ inset: '-14px' }} />
          {/* auto-adjusting message above the frame */}
          {autoFace && !faceOk && (
            <div className="absolute left-1/2 -translate-x-1/2 text-center" style={{ top: '-3.8rem', width: '90vw', maxWidth: '340px' }}>
              <span className={`inline-block text-white text-[18px] font-medium px-4 py-2 rounded-2xl shadow ${moving ? 'bg-red-600 animate-pulse' : 'bg-[#00A651]/90'}`}>
                {t('register.autoAdjusting')}
              </span>
            </div>
          )}
          {/* circular face guide (place your face inside the circle) */}
          <div
            className={`absolute rounded-full border-2 ${faceOk ? 'border-solid border-[#00A651]' : 'border-dashed border-white/80'}`}
            style={{ width: '84%', aspectRatio: '1 / 1', left: '8%', top: '17%' }}
          />
          {/* corner brackets */}
          {[
            'top-0 left-0 border-t-4 border-l-4 rounded-tl-lg',
            'top-0 right-0 border-t-4 border-r-4 rounded-tr-lg',
            'bottom-0 left-0 border-b-4 border-l-4 rounded-bl-lg',
            'bottom-0 right-0 border-b-4 border-r-4 rounded-br-lg',
          ].map((cls, i) => (
            <div key={i} className={`absolute ${cls} border-[#00A651]`} style={{ width: '22%', height: '18%' }} />
          ))}
        </div>
      </div>

      {/* Instruction */}
      <div className="absolute left-0 right-0 text-center" style={{ zIndex: 3, bottom: '7rem' }}>
        <p className="text-white text-sm bg-black/50 inline-block px-4 py-1.5 rounded-full">
          {autoFace && faceOk ? t('register.camHoldStill') : t('register.faceHint')}
        </p>
      </div>

      {/* Zoom slider */}
      <div className="absolute left-0 right-0 flex items-center justify-center gap-3 px-8" style={{ zIndex: 3, bottom: '11rem' }}>
        <span className="text-white text-lg font-bold w-5 text-center">−</span>
        <input
          type="range" min="0.5" max="2" step="0.05" value={zoom}
          onChange={(e) => setZoom(parseFloat(e.target.value))}
          className="flex-1 max-w-xs accent-[#00A651]"
          data-testid="face-camera-zoom"
        />
        <span className="text-white text-lg font-bold w-5 text-center">＋</span>
      </div>

      {/* Controls */}
      <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-8" style={{ zIndex: 3 }}>
        <button
          onClick={() => setFacingMode(m => (m === 'user' ? 'environment' : 'user'))}
          className="w-12 h-12 rounded-full bg-black/50 text-white flex items-center justify-center"
          data-testid="face-camera-flip"
        >
          <RotateCcw className="h-5 w-5" />
        </button>
        <button
          onClick={() => handleCapture(true)}
          disabled={!ready}
          className="relative w-16 h-16 rounded-full border-4 border-white flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform"
          data-testid="face-camera-capture-btn"
        >
          {autoFace && (
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 64 64" style={{ pointerEvents: 'none' }}>
              <circle cx="32" cy="32" r="30" fill="none" stroke="#00A651" strokeWidth="4"
                strokeDasharray={2 * Math.PI * 30}
                strokeDashoffset={(1 - faceProgress) * 2 * Math.PI * 30}
                strokeLinecap="round" />
            </svg>
          )}
          <div className="w-12 h-12 rounded-full bg-white" />
        </button>
        <div className="w-12 h-12" />
      </div>
    </div>
  );
}

export default FaceCamera;
